import React, { useRef, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function AudioPlayer({ filename, mediaType, onTimeUpdate }) {
  const mediaRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);
  const [streamUrl, setStreamUrl] = useState('');

  const isVideo = mediaType === 'video';

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(false);
    setStreamUrl('');

    if (!filename) return;

    const getUrl = async () => {
      const { data, error: urlError } = await supabase.storage
        .from('tracks')
        .createSignedUrl(filename, 3600);
      if (data?.signedUrl) setStreamUrl(data.signedUrl);
      if (urlError) setError(true);
    };
    getUrl();
  }, [filename]);

  const togglePlay = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    if (playing) {
      media.pause();
    } else {
      media.play().catch(() => {});
    }
  }, [playing]);

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const time = ratio * duration;
    if (mediaRef.current) {
      mediaRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const mediaEvents = {
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onTimeUpdate: (e) => {
      setCurrentTime(e.target.currentTime);
      onTimeUpdate?.(e.target.currentTime);
    },
    onLoadedMetadata: (e) => setDuration(e.target.duration),
    onEnded: () => setPlaying(false),
    onError: () => setError(true),
  };

  if (!streamUrl && !error) {
    return <div className="text-secondary text-sm" style={{ padding: '8px 0' }}>Loading audio...</div>;
  }

  if (isVideo) {
    return (
      <div className="video-player">
        <video
          key={streamUrl}
          ref={mediaRef}
          src={streamUrl}
          preload="metadata"
          controlsList="nodownload"
          className="video-element"
          onClick={togglePlay}
          {...mediaEvents}
        />
        <div className="player-controls">
          <button className="play-btn" onClick={togglePlay}>
            {playing ? '\u275A\u275A' : '\u25B6'}
          </button>
          <div style={{ flex: 1 }}>
            <div className="progress-bar" onClick={handleSeek}>
              <div className="progress" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <span className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-player">
      <audio
        key={streamUrl}
        ref={mediaRef}
        src={streamUrl}
        preload="metadata"
        controlsList="nodownload"
        {...mediaEvents}
      />

      {error && (
        <p className="text-secondary text-sm" style={{ color: 'var(--color-error, #ef4444)', marginTop: 4 }}>
          Failed to load audio. Try refreshing.
        </p>
      )}

      <div className="player-controls">
        <button className="play-btn" onClick={togglePlay}>
          {playing ? '\u275A\u275A' : '\u25B6'}
        </button>
        <div style={{ flex: 1 }}>
          <div className="progress-bar" onClick={handleSeek}>
            <div className="progress" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <span className="time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    </div>
  );
}

export { formatTime };
